<template>
  <section class="place-list" aria-label="Place list">
    <div class="datatable-toolbar place-toolbar">
      <div class="place-search">
        <label class="sr-only" for="place-search">Search places</label>
        <input id="place-search" type="search" class="form-control" placeholder="Search places" v-model="dataFilter.searchText" @keyup.enter="doFind">
        <button class="btn btn-outline-primary" type="button" @click="doFind">Search</button>
        <button v-if="dataFilter.searchText" class="btn btn-link text-secondary clear-search" type="button" @click="clearSearch">Clear</button>
      </div>

      <RouterLink to="/place/add" class="btn btn-primary add-place-button">
        <i class="pi pi-plus mr-2" aria-hidden="true"></i>
        Add place
      </RouterLink>
    </div>

    <div v-if="isLoading || dataList.length" class="table-responsive table-container place-table-wrapper">
      <table class="table table-striped place-table">
        <thead>
        <tr>
          <th scope="col">No</th>
          <th scope="col">Name</th>
          <th scope="col">Type</th>
          <th scope="col">Order</th>
          <th scope="col">Description</th>
          <th scope="col">Actions</th>
        </tr>
        </thead>

        <tbody>
        <tr v-if="isLoading">
          <td colspan="6" class="table-message">
            <span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
            Loading places…
          </td>
        </tr>
        <tr v-else v-for="(data, index) in dataList" :key="data.id">
          <td class="order-number">{{ index + 1 }}</td>
          <td class="text-left place-name">{{ data.name }}</td>
          <td class="text-left">{{ data.placeTypeName || '—' }}</td>
          <td class="text-center">{{ data.orderNumber ?? '—' }}</td>
          <td class="text-left place-description">{{ data.description || '—' }}</td>
          <td class="actions">
            <button type="button" class="btn btn-outline-secondary" :aria-label="`Edit ${data.name}`" v-tooltip="'Edit place'" @click="onEdit(data)"><i class="pi pi-pencil" aria-hidden="true"></i></button>
            <button type="button" class="btn btn-outline-danger" :aria-label="`Remove ${data.name}`" v-tooltip="'Remove place'" @click="onRemove(data)"><i class="pi pi-trash" aria-hidden="true"></i></button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="place-empty-state" role="status">
      <i class="pi pi-map-marker" aria-hidden="true"></i>
      <h2>{{ dataFilter.searchText ? 'No places match your search.' : 'No places yet.' }}</h2>
      <p>{{ dataFilter.searchText ? 'Try a different keyword or clear the search.' : 'Add a place to begin building your event location directory.' }}</p>
      <button v-if="dataFilter.searchText" class="btn btn-outline-primary" type="button" @click="clearSearch">Clear search</button>
      <RouterLink v-else to="/place/add" class="btn btn-primary">
        <i class="pi pi-plus mr-2" aria-hidden="true"></i>
        Add place
      </RouterLink>
    </div>

    <ConfirmDialog></ConfirmDialog>
  </section>
</template>

<script>
import {defineComponent} from 'vue'
import ConfirmDialog from "primevue/confirmdialog";
import apiService from "@/services/ApiService";

export default defineComponent({
  name: "PlaceList",
  components: {
    ConfirmDialog
  },
  data() {
    return {
      dataList: [],
      isLoading: true,
      dataFilter: {
        searchText: ''
      }
    }
  },
  methods: {
    onEdit(data) {
      this.$router.push(`/place/edit/${data.id}`);
    },
    onRemove(data) {
      this.$confirm.require({
        message: `Remove ${data.name} from the place directory?`,
        header: 'Remove place',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => this.doRemove(data.id)
      });
    },
    doFind() {
      const params = {
        SearchText: this.dataFilter.searchText
      };
      this.isLoading = true;
      apiService.get('/place', {params: params})
          .then(response => {
            this.dataList = response.data || [];
          })
          .catch(error => {
            this.$toast.add({
              severity: 'error',
              summary: 'Failed to get places',
              detail: error.response?.data?.Message || 'Please try again.',
              life: 5000
            });
          }).finally(() => {
            this.isLoading = false;
          });
    },
    clearSearch() {
      this.dataFilter.searchText = '';
      this.doFind();
    },
    doRemove(id) {
      apiService.delete(`/place/${id}`)
          .then(() => {
            this.doFind();
            this.$toast.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Place has been removed',
              life: 5000
            });
          })
          .catch(error => {
            this.$toast.add({
              severity: 'error',
              summary: 'Failed to remove place',
              detail: error.response?.data?.Message || 'Please try again.',
              life: 5000
            });
          });
    }
  },
  mounted() {
    this.doFind();
  }
})
</script>

<style scoped>
.place-toolbar {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.place-search {
  align-items: center;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.place-search .form-control {
  max-width: 19rem;
}

.clear-search {
  font-size: 0.875rem;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

.add-place-button {
  white-space: nowrap;
}

.place-table-wrapper {
  margin: 0;
}

.place-table th {
  white-space: nowrap;
}

.place-name {
  font-weight: 600;
  min-width: 13rem;
}

.place-description {
  min-width: 18rem;
}

.table-message {
  color: #6c757d;
  padding: 2.5rem 1rem !important;
  text-align: center;
}

.place-empty-state {
  align-items: center;
  border: 1px solid #e2e6ec;
  border-radius: 0.65rem;
  color: #6c757d;
  display: flex;
  flex-direction: column;
  padding: 3rem 1.5rem;
  text-align: center;
}

.place-empty-state > .pi {
  color: #426cc6;
  font-size: 1.9rem;
  margin-bottom: 0.85rem;
}

.place-empty-state h2 {
  color: #343a40;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.35rem;
}

.place-empty-state p {
  margin: 0 0 1rem;
}

@media (max-width: 767.98px) {
  .place-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .place-search .form-control {
    max-width: none;
    width: 100%;
  }

  .add-place-button {
    align-self: flex-start;
  }
}
</style>
